/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/**
 * Retrieves the current state and collection of feature flags from LaunchDarkly.
 *
 * @returns {Array} An array composed of:
 *   - isFlagUsable: A boolean indicating whether all feature flags have been successfully loaded.
 *   - flags: An object containing all the feature flags obtained from LaunchDarkly.
 *
 * @remarks
 * It's crucial to verify that LaunchDarkly is enabled, as certain environments (e.g., ECE) may not support it.
 */

import { useContext, useMemo } from 'react'
// eslint-disable-next-line no-restricted-imports
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isEmpty } from 'lodash'

import { useConfig } from '@modules/cui/ConfigContext'
import { getUserUsecase } from '@modules/profile-lib'
import type { UserProfileFromApi } from '@modules/ui-types'
import Feature from '@modules/utils/feature'

// eslint-disable-next-line import/no-restricted-paths
import { isElasticStaff } from '@/lib/validateEmail'

import { LaunchDarklyContext } from './LaunchDarklyContext'

import type { LDFlagSet } from 'launchdarkly-js-client-sdk'
import type { LDContext, LDMultiKindContext, LDSingleKindContext } from 'launchdarkly-js-sdk-common'

interface UseFlagsWhenLoadedProps {
  // Whether or not we should ignore stale flags while identifying
  // Setting this to "true" will ensure we only have the newest flags
  // but will cause a flicker in the "isFlagsUsable" state and also the flags
  // will be an empty object while identifying
  ignoreStaleFlags: boolean
}

export function useFlagsWhenLoaded(
  options?: UseFlagsWhenLoadedProps,
): [isFlagsUsable: boolean, flags: LDFlagSet] {
  const { ignoreStaleFlags = false } = options || {}
  const { clientInitialized, isIdentifying, clientInitializationFailed } =
    useContext(LaunchDarklyContext)
  const _flags = useFlags()

  const isLaunchDarklyEnabled = Boolean(useConfig(Feature.launchdarklyId))

  const flags = useMemo(() => {
    if (!clientInitialized) {
      return {}
    }

    // If we don't care about stale flags (when identifying), just return the old flags
    if (!ignoreStaleFlags) {
      return _flags
    }

    // Otherwise return an empty object while we are identifying
    if (isIdentifying) {
      return {}
    }

    return _flags
  }, [ignoreStaleFlags, clientInitialized, isIdentifying])

  const isFlagsUsable = useMemo(() => {
    if (clientInitializationFailed) {
      return true
    }

    return !isEmpty(flags)
  }, [clientInitializationFailed, flags])

  if (!isLaunchDarklyEnabled) {
    return [true, {}]
  }

  return [isFlagsUsable, flags]
}

interface LDClientStatus {
  isIdentifying: boolean
  clientInitialized: boolean
}

export function useIdentify(): [
  identify: (context: LDContext) => Promise<void>,
  status: LDClientStatus,
] {
  const { identify, isIdentifying, clientInitialized } = useContext(LaunchDarklyContext)
  return [identify, { isIdentifying, clientInitialized }]
}

export function useServerlessFeatureFlag(): boolean {
  return useConfig(Feature.serverless)
}

export type ResourceContext = {
  key: string
  type: 'deployment' | 'project'
  solutionType: string
  cloudProvider: string
  region: string
}

const getUserFieldsFromProfile = (profile: UserProfileFromApi) => ({
  key: profile.user_id.toString(),
  domain: profile.domain,
  inTrial: profile.is_trial,
  ...(profile.organization_id ? { organization: profile.organization_id } : {}),
  isElasticStaff: isElasticStaff(profile.email),
  useCase: getUserUsecase(profile),
  tags: getLaunchDarklyProfileTags(profile),
})

export const makeUserLDContext = (profile: UserProfileFromApi): LDSingleKindContext => ({
  kind: 'user',
  ...getUserFieldsFromProfile(profile),
})

export const makeMultiLDContextWithResource = (
  profile: UserProfileFromApi,
  resource: ResourceContext,
): LDMultiKindContext => ({
  kind: 'multi',
  user: getUserFieldsFromProfile(profile),
  resource,
})

/**
 * Returns user tags that can be used in LaunchDarkly rules without really identifying the user,
 * this way we don't need to send their entire email addresses over the wire and avoid privacy concerns.
 */
const getLaunchDarklyProfileTags = (profile: UserProfileFromApi): string[] => {
  const cypressRegexp = new RegExp(/^cypresstest\+register-test-.*@elasticsearch\.com$/)
  const isCypressTag = cypressRegexp.test(profile.email) ? ['cypress-user'] : []

  return isCypressTag
}
